/* ----------------------------------------------------------------------------
 * ControlStateAdornment — the small state signal that flanks the push-to-talk
 * label inside the Aura popup control. Driven purely by `status`:
 *   - listening : teal responsive waveform (reuses AuraWaveform via currentColor)
 *   - speaking  : amber waveform (lively generic — see note in the build doc:
 *                 no analyser node is exposed on AuraAudioPlayer, so tapping real
 *                 per-sample amplitude would mean rewiring the just-hardened
 *                 playback graph; the waveform is instead gated on/off to REAL
 *                 speaking status, animating only while audio actually plays)
 *   - thinking  : violet processing dots (opacity+scale pulse)
 *   - idle/error: nothing (returns null -> the adornment unmounts on change)
 *
 * VoiceCopilot renders one on each side of the label. Reduced-motion falls back
 * to static bars/dots (handled in AuraWaveform + CONTROL_DOTS_CSS).
 * -------------------------------------------------------------------------- */
import { useEffect } from 'react';
import { AuraWaveform } from '../AuraWaveform.tsx';
import {
  CONTROL_DOTS_CSS,
  CONTROL_DOTS_STYLE_ID,
  STATE_ACCENTS,
  controlAdornment,
  type VoiceStatus,
} from './stateVisualStyle.ts';

function ensureDotStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(CONTROL_DOTS_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = CONTROL_DOTS_STYLE_ID;
  el.textContent = CONTROL_DOTS_CSS;
  document.head.appendChild(el);
}

const DOT_DELAYS = [0, 150, 300, 450];

function ProcessingDots({ color }: { color: string }) {
  ensureDotStyles();
  useEffect(() => { ensureDotStyles(); }, []);
  return (
    <span className="bc-dots" style={{ color }} aria-hidden>
      {DOT_DELAYS.map((d, i) => (
        <i key={i} style={{ animationDelay: `${d}ms` }} />
      ))}
    </span>
  );
}

export interface ControlStateAdornmentProps {
  status: VoiceStatus;
  className?: string;
}

export function ControlStateAdornment({ status, className }: ControlStateAdornmentProps) {
  const kind = controlAdornment(status);
  if (kind === 'none') return null;

  const color = STATE_ACCENTS[status].accent;

  if (kind === 'dots') {
    return (
      <span className={className}>
        <ProcessingDots color={color} />
      </span>
    );
  }

  // waveform (listening + speaking) — AuraWaveform inherits currentColor.
  return (
    <span className={className} style={{ color }} aria-hidden>
      <AuraWaveform bars={4} heightPx={16} active />
    </span>
  );
}
